import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../sections/Navbar";
import HighlightSection from "../sections/HighlightSection";
import BrandStatement from "../sections/BrandStatement";
import ServicesSection from "../sections/ServicesSection";
import StatsBar from "../sections/StatsBar";
import MessageSection from "../sections/MessageSection";
import styles from "./LandingPage.module.css";

/* ─── Portfolio data ─── */
const portfolioItems = [
  { title: "Residential", subtitle: "Homes & Apartments", image: "/media/r1 copy.jpg", link: "/projects?category=Residential", count: "5 Projects" },
  { title: "Commercial",  subtitle: "Offices & Retail",   image: "/commercial/PHOTO-2026-07-08-13-58-29.jpg", link: "/projects?category=Commercial", count: "4 Projects" },
  { title: "Renovations", subtitle: "Transformations",    image: "/media/l1 copy.jpg", link: "/projects?category=Renovations", count: "2 Projects" },
];

/* ─── Selected Works strip ─── */
const works = [
  { label: "Contemporary Living", image: "/media/1 copy.jpg" },
  { label: "Executive Suite",     image: "/media/b1 copy.jpg" },
  { label: "Corporate HQ",        image: "/commercial/PHOTO-2026-07-08-13-58-29_4.jpg" },
  { label: "Urban Loft",          image: "/media/l1 copy.jpg" },
  { label: "Luxury Kitchen",      image: "/media/5 copy.jpg" },
];

/* ─── Marquee hook for "works" strip ─── */
const WorkStrip = () => (
  <div className={styles.workStrip}>
    <div className={styles.workTrack}>
      {[...works, ...works].map((w, i) => (
        <div key={i} className={styles.workItem}>
          <img src={w.image} alt={w.label} />
          <span>{w.label}</span>
        </div>
      ))}
    </div>
  </div>
);

const LandingPage = () => {
  /* Simple scroll-reveal: add .visible when element enters viewport */
  const revealRefs = useRef([]);
  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add(styles.visible); }),
      { threshold: 0.15 }
    );
    revealRefs.current.forEach((el) => el && obs.observe(el));
    return () => obs.disconnect();
  }, []);
  const reveal = (i) => (el) => { revealRefs.current[i] = el; };

  return (
    <div className={styles.page}>
      <Navbar />

      {/* ══ 1. CINEMATIC HERO ══ */}
      <section className={styles.hero}>
        <video autoPlay muted loop playsInline className={styles.heroBg}>
          <source src="/create_a_banner_vedio.mp4" type="video/mp4" />
        </video>
        <div className={styles.heroOverlay} />
        <div className={styles.heroInner}>
          <div className={styles.heroContent}>
            <span className={styles.heroEyebrow}>Sakha Design Lab</span>
            <h1 className={styles.heroHeadline}>
              Spaces That<br /><strong>Inspire.</strong>
            </h1>
            <p className={styles.heroSub}>
              Premium interior design for homes and commercial spaces across India.
            </p>
            <div className={styles.heroCtas}>
              <Link to="/projects" className={styles.ctaPrimary}>View Our Work</Link>
              <Link to="/contact"  className={styles.ctaSecondary}>Get In Touch</Link>
            </div>
          </div>
          <MessageSection
            id="hero-message-section"
            hideTitle={true}
            className={styles.heroContactForm}
            formSource="Landing Page Hero Contact Form"
          />
        </div>
        <div className={styles.scrollHint}>
          <span />
          <p>Scroll</p>
        </div>
      </section>

      {/* ══ 2. TRUST STRIP ══ */}
      <HighlightSection />

      {/* ══ 3. BRAND STATEMENT ══ */}
      <div ref={reveal(0)} className={styles.revealBlock}>
        <BrandStatement />
      </div>

      {/* ══ 4. WORK MARQUEE STRIP ══ */}
      <WorkStrip />

      {/* ══ 5. SERVICES ══ */}
      <div ref={reveal(1)} className={styles.revealBlock}>
        <ServicesSection />
      </div>

      {/* ══ 6. PORTFOLIO GRID ══ */}
      <section ref={reveal(2)} className={`${styles.portfolio} ${styles.revealBlock}`}>
        <div className={styles.portfolioHeader}>
          <div>
            <span className={styles.sectionTag}>PORTFOLIO</span>
            <h2 className={styles.sectionTitle}>Our Work<br />Speaks For Itself</h2>
          </div>
          <Link to="/projects" className={styles.linkUnderline}>View All Projects →</Link>
        </div>
        <div className={styles.portfolioGrid}>
          <Link to={portfolioItems[0].link} className={`${styles.pCard} ${styles.pCardTall}`}>
            <img src={portfolioItems[0].image} alt={portfolioItems[0].title} />
            <div className={styles.pOverlay}>
              <span className={styles.pCount}>{portfolioItems[0].count}</span>
              <h3 className={styles.pTitle}>{portfolioItems[0].title}</h3>
              <p  className={styles.pSub}>{portfolioItems[0].subtitle}</p>
              <span className={styles.pCta}>View Projects →</span>
            </div>
          </Link>
          <div className={styles.pStack}>
            {portfolioItems.slice(1).map((p, i) => (
              <Link to={p.link} key={i} className={`${styles.pCard} ${styles.pCardHalf}`}>
                <img src={p.image} alt={p.title} />
                <div className={styles.pOverlay}>
                  <span className={styles.pCount}>{p.count}</span>
                  <h3 className={styles.pTitle}>{p.title}</h3>
                  <p  className={styles.pSub}>{p.subtitle}</p>
                  <span className={styles.pCta}>View Projects →</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ══ 7. MID-PAGE VIDEO BANNER ══ */}
      <section className={styles.midBanner}>
        <video autoPlay muted loop playsInline className={styles.midBannerBg}>
          <source src="/create_a_banner_vedio.mp4" type="video/mp4" />
        </video>
        <div className={styles.midBannerOverlay} />
        <div className={styles.midBannerContent}>
          <span className={styles.midBannerEyebrow}>CRAFTED WITH INTENTION</span>
          <h2 className={styles.midBannerHeadline}>CRAFTED BEAUTY</h2>
          <Link to="/projects" className={styles.ctaSecondary} style={{ marginTop: "20px" }}>Explore Our Work</Link>
        </div>
      </section>

      {/* ══ 8. STATS BAR ══ */}
      <StatsBar />

      {/* ══ 9. TESTIMONIAL / QUOTE ══ */}
      <section ref={reveal(3)} className={`${styles.quote} ${styles.revealBlock}`}>
        <blockquote className={styles.quoteText}>
          "Every project is a conversation between the space and the people who live in it.
          We make sure that conversation is beautiful."
        </blockquote>
        <cite className={styles.quoteCite}>— The ShaKa Design Lab Team</cite>
      </section>

      {/* ══ 10. CONTACT FORM ══ */}
      <div ref={reveal(4)} className={styles.revealBlock}>
        <MessageSection formSource="Landing Page Contact Section" />
      </div>

      {/* ══ 11. FOOTER ══ */}
      <footer className={styles.footer}>
        <div className={styles.footerLeft}>
          <span className={styles.footerBrand}>Sakha Design Lab</span>
          <p className={styles.footerTagline}>Transforming spaces. Inspiring lives.</p>
        </div>
        <div className={styles.footerRight}>
          <div className={styles.footerLinks}>
            <Link to="/">Home</Link>
            <Link to="/projects">Projects</Link>
            <Link to="/process">Process</Link>
            <Link to="/contact">Contact</Link>
          </div>
          <p className={styles.footerCopy}>©{new Date().getFullYear()} ShaKa Designs LLP. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
